'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from './AuthContext';

interface Tenant {
  id: string;
  company_name: string | null;
  slug: string | null;
  logo_url: string | null;
  primary_color: string;
  whatsapp_number: string | null;
  plan: 'starter' | 'pro' | 'elite';
  billing_cycle: string;
  tenant_status: string;
  trial_ends_at: string | null;
  vehicle_count: number;
  onboarding_completed: boolean;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  website_published?: boolean;
  hero_title?: string | null;
  hero_subtitle?: string | null;
  accent_color?: string | null;
  currency?: string;
  description?: string | null;
  tagline?: string | null;
  onboarding_step?: number;
}

interface TenantContextType {
  tenant: Tenant | null;
  profile: any | null;
  loading: boolean;
  isPro: boolean;
  isElite: boolean;
  isStarter: boolean;
  isTrialing: boolean;
  isActive: boolean;
  trialDaysLeft: number;
  refreshTenant: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType>({
  tenant: null,
  profile: null,
  loading: true,
  isPro: false,
  isElite: false,
  isStarter: true,
  isTrialing: false,
  isActive: false,
  trialDaysLeft: 0,
  refreshTenant: async () => {},
});

export const useTenant = () => useContext(TenantContext);

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  const fetchTenant = async () => {
    if (!user) {
      setTenant(null);
      setProfile(null);
      setLoading(false);
      return;
    }

    try {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      setProfile(profileData);

      if (profileData?.tenant_id) {
        const { data: tenantData } = await supabase
          .from('tenants')
          .select('*')
          .eq('id', profileData.tenant_id)
          .single();

        setTenant(tenantData);
      }
    } catch (err) {
      console.error('Error fetching tenant:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenant();
  }, [user]);

  const isElite = tenant?.plan === 'elite';
  const isPro = tenant?.plan === 'pro' || isElite;
  const isStarter = tenant?.plan === 'starter';
  const isTrialing = !!(tenant?.trial_ends_at && new Date(tenant.trial_ends_at) > new Date());
  const isActive = tenant?.tenant_status === 'active' || tenant?.tenant_status === 'trialing';

  const trialDaysLeft = tenant?.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(tenant.trial_ends_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;

  return (
    <TenantContext.Provider value={{
      tenant,
      profile,
      loading,
      isPro,
      isElite,
      isStarter,
      isTrialing,
      isActive,
      trialDaysLeft,
      refreshTenant: fetchTenant,
    }}>
      {children}
    </TenantContext.Provider>
  );
}
